import { motion } from 'motion/react';
import { useState } from 'react';
import { cn } from '@/lib/utils';
import { PROJECT_NAME } from '@/lib/config';
import {
  Scale, ShieldCheck, FileText, Sparkles, Bot, Lock,
  ArrowRight, CheckCircle2, MessageSquareText, Workflow,
  FileSearch, FileCog, Zap, Eye, Quote,
} from 'lucide-react';

// Ukrainian flag colors
const UA_BLUE = '#0057B7';
const UA_YELLOW = '#FFD700';

interface LandingProps {
  onSignIn: () => void;
  onSignUp: () => void;
}

export function Landing({ onSignIn, onSignUp }: LandingProps) {
  return (
    <div className="min-h-screen bg-[var(--t-bg)] text-white font-sans overflow-x-hidden">
      <BackgroundOrbs />
      <NavBar onSignIn={onSignIn} onSignUp={onSignUp} />
      <Hero onSignUp={onSignUp} />
      <FeatureGrid />
      <HowItWorks />
      <Capabilities />
      <SecuritySection />
      <CTA onSignUp={onSignUp} />
      <Footer />
    </div>
  );
}

function BackgroundOrbs() {
  return (
    <div className="fixed inset-0 -z-10 overflow-hidden pointer-events-none">
      <div
        className="absolute top-[-15%] left-[-10%] w-[55vw] h-[55vw] rounded-full opacity-30 blur-[120px]"
        style={{ background: `radial-gradient(circle, ${UA_BLUE} 0%, transparent 70%)` }}
      />
      <div
        className="absolute bottom-[-20%] right-[-10%] w-[45vw] h-[45vw] rounded-full opacity-20 blur-[120px]"
        style={{ background: `radial-gradient(circle, ${UA_YELLOW} 0%, transparent 70%)` }}
      />
      <div
        className="absolute top-[40%] left-[60%] w-[30vw] h-[30vw] rounded-full opacity-15 blur-[100px]"
        style={{ background: `radial-gradient(circle, ${UA_BLUE} 0%, transparent 70%)` }}
      />
    </div>
  );
}

function NavBar({ onSignIn, onSignUp }: LandingProps) {
  return (
    <header className="sticky top-0 z-30 backdrop-blur-md bg-[var(--t-bg)] border-b border-white/5">
      <div className="max-w-7xl mx-auto flex items-center justify-between px-6 lg:px-10 py-4">
        <a href="#top" className="flex items-center gap-2.5 group">
          <div
            className="w-9 h-9 rounded-xl flex items-center justify-center shadow-lg shadow-blue-900/40 group-hover:scale-105 transition-transform"
            style={{ background: `linear-gradient(135deg, ${UA_BLUE} 0%, ${UA_YELLOW} 130%)` }}
          >
            <Scale className="w-5 h-5 text-white" />
          </div>
          <div>
            <div className="font-bold tracking-tight text-white">{PROJECT_NAME}</div>
            <div className="text-[10px] uppercase tracking-widest text-white/40">Цифровий помічник</div>
          </div>
        </a>

        <nav className="hidden md:flex items-center gap-8 text-sm text-white/60">
          <a href="#features" className="hover:text-white transition-colors">Можливості</a>
          <a href="#how" className="hover:text-white transition-colors">Як це працює</a>
          <a href="#capabilities" className="hover:text-white transition-colors">Що вміє</a>
          <a href="#security" className="hover:text-white transition-colors">Безпека</a>
        </nav>

        <div className="flex items-center gap-2">
          <button
            onClick={onSignIn}
            className="px-4 py-2 text-sm text-white/80 hover:text-white transition-colors"
          >
            Увійти
          </button>
          <button
            onClick={onSignUp}
            className="px-5 py-2 rounded-full text-sm font-semibold text-[#0a0a0c] shadow-lg shadow-yellow-900/30 hover:scale-105 transition-transform"
            style={{ background: UA_YELLOW }}
          >
            Спробувати
          </button>
        </div>
      </div>
    </header>
  );
}

function Hero({ onSignUp }: { onSignUp: () => void }) {
  return (
    <section id="top" className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-20 lg:pt-32 pb-20">
      <div className="grid lg:grid-cols-2 gap-12 items-center">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.1 }}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 mb-6"
          >
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75" style={{ background: UA_YELLOW }} />
              <span className="relative inline-flex rounded-full h-2 w-2" style={{ background: UA_YELLOW }} />
            </span>
            <span className="text-xs text-white/70">Новий формат юридичної допомоги — для України</span>
          </motion.div>

          <h1 className="text-5xl lg:text-6xl xl:text-7xl font-bold tracking-tight leading-[1.05]">
            Твій цифровий
            <br />
            <span
              className="bg-clip-text text-transparent"
              style={{ backgroundImage: `linear-gradient(90deg, ${UA_BLUE}, ${UA_YELLOW})` }}
            >
              юрист 24/7
            </span>
          </h1>

          <p className="mt-6 text-lg lg:text-xl text-white/60 leading-relaxed max-w-xl">
            Опиши ситуацію — отримай готовий до подачі судовий документ.
            Бот веде розмову українською, аналізує ваші документи (PDF, DOCX,
            фото, скани з OCR), збирає реквізити та готує позов або заяву
            точно за формою.
          </p>

          <div className="mt-8 flex flex-wrap items-center gap-3">
            <button
              onClick={onSignUp}
              className="group flex items-center gap-2 px-7 py-3.5 rounded-full font-semibold text-[#0a0a0c] shadow-xl shadow-yellow-900/30 hover:scale-105 transition-transform"
              style={{ background: UA_YELLOW }}
            >
              Створити аккаунт безкоштовно
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </button>
            <a href="#how" className="px-6 py-3.5 rounded-full text-sm text-white/80 hover:text-white border border-white/10 hover:border-white/20 transition-colors">
              Як це працює →
            </a>
          </div>

          <div className="mt-10 flex items-center gap-6 text-xs text-white/40">
            <div className="flex items-center gap-1.5">
              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" /> Без прив'язки до банку
            </div>
            <div className="flex items-center gap-1.5">
              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" /> Приватно
            </div>
            <div className="flex items-center gap-1.5">
              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" /> Українською
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.7, delay: 0.2 }}
          className="relative"
        >
          <HeroIllustration />
        </motion.div>
      </div>
    </section>
  );
}

function HeroIllustration() {
  // Mock chat conversation that "types" like a real LLM stream
  return (
    <div className="relative">
      <div
        className="absolute -inset-4 rounded-[2rem] opacity-30 blur-2xl"
        style={{ background: `linear-gradient(135deg, ${UA_BLUE} 0%, ${UA_YELLOW} 100%)` }}
      />
      <div className="relative bg-[var(--t-panel)] border border-white/10 rounded-[2rem] p-6 shadow-2xl shadow-black/60">
        <div className="flex items-center gap-2 pb-4 border-b border-white/10 mb-4">
          <div className="w-2.5 h-2.5 rounded-full bg-red-500/60" />
          <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/60" />
          <div className="w-2.5 h-2.5 rounded-full bg-emerald-500/60" />
          <div className="ml-3 text-[11px] text-white/30 uppercase tracking-widest">{PROJECT_NAME}</div>
        </div>

        <ChatBubble who="user" delay={0.3}>
          Орендодавець не повертає заставу 20 000 грн вже місяць.
        </ChatBubble>
        <ChatBubble who="bot" delay={0.9}>
          Підготуємо позовну заяву. Розкажіть, з якого числа порушено умови та чи є переписка з орендодавцем?
        </ChatBubble>
        <ChatBubble who="user" delay={1.6}>
          З 12 січня. Є переписка та квитанції.
        </ChatBubble>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 2.2 }}
          className="mt-4 flex items-center gap-2 px-3 py-2 rounded-xl bg-emerald-500/10 border border-emerald-500/30"
        >
          <FileText className="w-4 h-4 text-emerald-400" />
          <span className="text-xs text-emerald-300 font-medium">Позовна-заява-про-розірвання-договору-оренди.docx</span>
          <Sparkles className="w-3.5 h-3.5 text-emerald-400 ml-auto" />
        </motion.div>
      </div>
    </div>
  );
}

function ChatBubble({ who, delay, children }: { who: 'user' | 'bot'; delay: number; children: React.ReactNode }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.4 }}
      className={cn('mb-3 flex gap-3', who === 'user' ? 'justify-end' : 'justify-start')}
    >
      {who === 'bot' && (
        <div
          className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
          style={{ background: UA_BLUE }}
        >
          <Scale className="w-4 h-4 text-white" />
        </div>
      )}
      <div
        className={cn(
          'rounded-2xl px-4 py-2.5 text-sm max-w-[75%]',
          who === 'user' ? 'bg-white/10 rounded-tr-sm' : 'bg-white/5 border border-white/10 rounded-tl-sm'
        )}
      >
        {children}
      </div>
      {who === 'user' && (
        <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center text-xs text-white/60 shrink-0">
          Ви
        </div>
      )}
    </motion.div>
  );
}

const FEATURES = [
  { icon: Bot, title: 'Розмова українською', text: 'Бот веде природний діалог, ставить уточнюючі запитання, не плутається у відмінках.' },
  { icon: FileSearch, title: 'Аналіз ваших документів', text: 'PDF, DOCX, MD, TXT, скани з OCR (Tesseract). Витягує реквізити та цитує текст.' },
  { icon: Workflow, title: 'Авто-збір реквізитів', text: 'Розпізнає ПІБ, адреси, дати, суми, ЄДРПОУ — заповнює праву панель автоматично.' },
  { icon: FileCog, title: 'Генерація .docx', text: 'Готовий документ у форматі суду — назвою з датою та кодом справи.' },
  { icon: Eye, title: 'Перегляд + правки', text: 'Виділяй текст, додавай зауваження, бот переписує саме ці фрагменти.' },
  { icon: Zap, title: 'Стрімінг у реальному часі', text: 'Відповіді з`являються поступово, без завмирань і таймаутів.' },
];

function FeatureGrid() {
  return (
    <section id="features" className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
      <SectionHeader
        eyebrow="Можливості"
        title="Все необхідне в одному вікні"
        subtitle="Не просто чат-бот — повноцінна робоча станція юриста, що економить години роботи."
      />
      <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {FEATURES.map((f, i) => (
          <motion.div
            key={f.title}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-50px' }}
            transition={{ delay: i * 0.05 }}
            className="group relative p-6 rounded-2xl bg-white/[0.02] border border-white/10 hover:border-white/20 hover:bg-white/[0.04] transition-all"
          >
            <div
              className="absolute -top-3 -left-3 w-10 h-10 rounded-xl flex items-center justify-center shadow-lg"
              style={{ background: i % 2 === 0 ? UA_BLUE : UA_YELLOW }}
            >
              <f.icon className={cn('w-5 h-5', i % 2 === 0 ? 'text-white' : 'text-[#0a0a0c]')} />
            </div>
            <h3 className="mt-4 text-base font-semibold text-white">{f.title}</h3>
            <p className="mt-2 text-sm text-white/50 leading-relaxed">{f.text}</p>
          </motion.div>
        ))}
      </div>
    </section>
  );
}

const STEPS = [
  { n: 1, title: 'Опиши ситуацію', text: 'Українською мовою своїми словами. Бот сам ставитиме уточнюючі запитання.' },
  { n: 2, title: 'Завантаж документи', text: 'Договір, переписку, фото — навіть скани розпізнаються через OCR.' },
  { n: 3, title: 'Перевір реквізити', text: 'У правій панелі поля наповнюються автоматично. Ти можеш правити будь-яке кліком.' },
  { n: 4, title: 'Згенеруй та скачай', text: 'Готовий .docx — підпиши, оплати збір і подавай до суду.' },
];

function HowItWorks() {
  return (
    <section id="how" className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
      <SectionHeader eyebrow="Як це працює" title="Чотири кроки до готового позову" />
      <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-4 gap-5">
        {STEPS.map((s, i) => (
          <motion.div
            key={s.n}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.1 }}
            className="relative"
          >
            <div className="flex items-center gap-3 mb-3">
              <div
                className="w-10 h-10 rounded-full flex items-center justify-center font-bold text-lg"
                style={{ background: UA_YELLOW, color: '#0a0a0c' }}
              >
                {s.n}
              </div>
              {i < STEPS.length - 1 && (
                <div className="hidden md:block h-px flex-1 bg-gradient-to-r from-white/20 to-transparent" />
              )}
            </div>
            <h3 className="text-lg font-semibold text-white">{s.title}</h3>
            <p className="mt-2 text-sm text-white/50 leading-relaxed">{s.text}</p>
          </motion.div>
        ))}
      </div>
    </section>
  );
}

const CAPABILITIES_LEFT = [
  'Позовна заява про розірвання договору',
  'Скарга на бездіяльність органу влади',
  'Заява про стягнення заборгованості',
  'Претензія до контрагента',
  'Заперечення проти позову',
  'Клопотання, апеляційні та касаційні скарги',
];

const CAPABILITIES_RIGHT = [
  'OCR українською (Tesseract + ukr+rus+eng)',
  'Розпізнавання сканованих PDF',
  'Витяг реквізитів з договорів',
  'Цитування статей ЦК, ЦПК, ЖК України',
  'Стрімінг тексту як у ChatGPT',
  'Хмарне збереження ваших документів',
];

function Capabilities() {
  return (
    <section id="capabilities" className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
      <div className="grid lg:grid-cols-2 gap-12 items-center">
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
        >
          <SectionHeader
            eyebrow="Що вміє"
            title="Документи на будь-який випадок"
            align="left"
            subtitle="База шаблонів постійно розширюється. Можна завантажити власний еталонний документ — бот навчиться генерувати такі."
          />
        </motion.div>
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          className="grid sm:grid-cols-2 gap-4"
        >
          <div className="space-y-2.5">
            {CAPABILITIES_LEFT.map((c) => (
              <div key={c} className="flex items-start gap-2 text-sm text-white/70">
                <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" style={{ color: UA_YELLOW }} />
                {c}
              </div>
            ))}
          </div>
          <div className="space-y-2.5">
            {CAPABILITIES_RIGHT.map((c) => (
              <div key={c} className="flex items-start gap-2 text-sm text-white/70">
                <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0 text-emerald-400" />
                {c}
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function SecuritySection() {
  return (
    <section id="security" className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
      <SectionHeader eyebrow="Безпека" title="Ваші справи — лише ваші" />
      <div className="mt-14 grid md:grid-cols-3 gap-5">
        {[
          { icon: Lock, title: 'Шифрування', text: 'Файли передаються лише через HTTPS. Документи в обʼєктному сховищі MinIO з приватними URL.' },
          { icon: ShieldCheck, title: 'Ізоляція справ', text: 'Сесії привʼязані до вашого акаунта. Інший користувач не може побачити чи завантажити ваш документ.' },
          { icon: MessageSquareText, title: 'Прозоро', text: 'Бачите кожне поле, кожен витяг з документа. Можете редагувати або видалити будь-що.' },
        ].map((c, i) => (
          <motion.div
            key={c.title}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.1 }}
            className="p-7 rounded-2xl border border-white/10 bg-gradient-to-br from-white/[0.03] to-transparent"
          >
            <c.icon className="w-7 h-7 mb-4" style={{ color: UA_YELLOW }} />
            <h3 className="text-lg font-semibold text-white">{c.title}</h3>
            <p className="mt-2 text-sm text-white/50 leading-relaxed">{c.text}</p>
          </motion.div>
        ))}
      </div>
    </section>
  );
}

function CTA({ onSignUp }: { onSignUp: () => void }) {
  return (
    <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
      <div
        className="cta-light-text relative overflow-hidden rounded-[2rem] px-8 lg:px-16 py-16 text-center"
        style={{
          background: `linear-gradient(135deg, ${UA_BLUE} 0%, #001a3a 60%, ${UA_YELLOW} 220%)`,
        }}
      >
        <div className="absolute inset-0 opacity-30" style={{
          background: 'radial-gradient(circle at 30% 30%, rgba(255,215,0,0.4) 0%, transparent 50%), radial-gradient(circle at 70% 70%, rgba(0,87,183,0.4) 0%, transparent 50%)'
        }} />
        <div className="relative">
          <Quote className="w-8 h-8 mx-auto mb-4 text-white/40" />
          <h2 className="text-3xl lg:text-5xl font-bold tracking-tight max-w-2xl mx-auto leading-tight">
            Не плати за консультацію годину. Підготуй позов за 10 хвилин.
          </h2>
          <p className="mt-5 text-white/70 max-w-xl mx-auto">
            Безкоштовний акаунт — вистачить для перших справ. Без банківської картки.
          </p>
          <button
            onClick={onSignUp}
            className="mt-8 inline-flex items-center gap-2 px-8 py-4 rounded-full text-base font-semibold text-[#0a0a0c] shadow-xl shadow-black/40 hover:scale-105 transition-transform"
            style={{ background: UA_YELLOW }}
          >
            Зареєструватися
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="border-t border-white/5 mt-12">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-8 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-white/30">
        <div className="flex items-center gap-2">
          <Scale className="w-4 h-4" />
          <span>{PROJECT_NAME} · Слава Україні</span>
          <span aria-hidden className="text-white/20">·</span>
          <span className="inline-flex items-center gap-0.5">
            <span className="w-2 h-3 rounded-sm" style={{ background: UA_BLUE }} />
            <span className="w-2 h-3 rounded-sm" style={{ background: UA_YELLOW }} />
          </span>
        </div>
        <div>© {new Date().getFullYear()} · AI може помилятися — перевіряйте юридично значущу інформацію.</div>
      </div>
    </footer>
  );
}

function SectionHeader({
  eyebrow,
  title,
  subtitle,
  align = 'center',
}: {
  eyebrow?: string;
  title: string;
  subtitle?: string;
  align?: 'left' | 'center';
}) {
  return (
    <div className={cn(align === 'center' ? 'text-center' : 'text-left max-w-xl')}>
      {eyebrow && (
        <div
          className="inline-block text-xs font-bold uppercase tracking-[0.2em] mb-3"
          style={{ color: UA_YELLOW }}
        >
          {eyebrow}
        </div>
      )}
      <h2 className="text-3xl lg:text-5xl font-bold tracking-tight">{title}</h2>
      {subtitle && (
        <p className={cn('mt-4 text-white/50 text-lg leading-relaxed', align === 'center' && 'max-w-2xl mx-auto')}>
          {subtitle}
        </p>
      )}
    </div>
  );
}

// Suppress unused variable warnings for icons used inline above
void useState;
